/**
 * Non-interactive chart overlays, extracted from Chart.tsx: the Weekly
 * Behaviour session bands and the closed-trade history markers. Both are
 * pure SVG renderers — no drag/menu wiring — so they take their inputs
 * (candles, runs, the time→x converter, series) as explicit params and
 * return SVG to drop into the overlay <svg>.
 */
import type { ReactNode } from "react";
import { bucketStartSec } from "@/hooks/barBucket";
import type { Candle, Timeframe } from "@/api/types";
import type { RoundTrip } from "@/components/PositionsPanel/PositionsPanel";
import type { AnySeries } from "./chartData";

type TimeToX = (time: number) => number | null;

/** A contiguous run of candles sharing a Weekly-Behaviour band colour.
 *  `color === null` = the run exists (for seam math) but renders nothing. */
export interface WeeklyBandRun {
  key: string;
  firstIdx: number;
  lastIdx: number;
  color: string | null;
}

export function renderWeeklyBands(
  weeklyBandRuns: WeeklyBandRun[],
  candles: Candle[],
  timeToX: TimeToX,
): ReactNode {
  if (weeklyBandRuns.length === 0) return null;
  const n = candles.length;
  // Helper: midpoint X between two candles. Used at the seam between
  // adjacent runs so the colour change lands inside the weekend gap (or
  // between any two consecutive bars) rather than inside one of the bars.
  const seamX = (leftIdx: number, rightIdx: number): number | null => {
    const xa = timeToX(candles[leftIdx].time);
    const xb = timeToX(candles[rightIdx].time);
    if (xa == null || xb == null) return null;
    return (xa + xb) / 2;
  };
  // Edge X: just outside the first/last candle. Without knowing barSpacing
  // exactly we use half the distance to the next candle as a stand-in.
  const leftEdgeX = (idx: number): number | null => {
    const x0 = timeToX(candles[idx].time);
    if (x0 == null) return null;
    if (n <= 1) return x0;
    const x1 = timeToX(candles[Math.min(idx + 1, n - 1)].time);
    if (x1 == null) return x0;
    return x0 - Math.abs(x1 - x0) / 2;
  };
  const rightEdgeX = (idx: number): number | null => {
    const x0 = timeToX(candles[idx].time);
    if (x0 == null) return null;
    if (n <= 1) return x0;
    const xPrev = timeToX(candles[Math.max(idx - 1, 0)].time);
    if (xPrev == null) return x0;
    return x0 + Math.abs(x0 - xPrev) / 2;
  };

  // Index runs by tag (d vs w) so the seam between two same-tag runs uses
  // the gap between the outgoing run's lastIdx and the incoming firstIdx.
  const byTag: Record<"d" | "w", WeeklyBandRun[]> = {
    d: weeklyBandRuns.filter((r) => r.key.startsWith("d-")),
    w: weeklyBandRuns.filter((r) => r.key.startsWith("w-")),
  };
  const out: JSX.Element[] = [];
  (Object.keys(byTag) as ("d" | "w")[]).forEach((tag) => {
    const list = byTag[tag];
    list.forEach((run, i) => {
      // Disabled runs occupy a slot (so seam math is correct for
      // neighbours) but don't render a rect.
      if (!run.color) return;
      const prev = list[i - 1];
      const next = list[i + 1];
      const lx = prev
        ? seamX(prev.lastIdx, run.firstIdx)
        : leftEdgeX(run.firstIdx);
      const rx = next
        ? seamX(run.lastIdx, next.firstIdx)
        : rightEdgeX(run.lastIdx);
      if (lx == null || rx == null) return;
      const left = Math.min(lx, rx);
      const w = Math.abs(rx - lx);
      if (w <= 0) return;
      out.push(
        <rect
          key={run.key}
          x={left}
          y={0}
          width={w}
          height="100%"
          fill={run.color}
          pointerEvents="none"
        />,
      );
    });
  });
  return out;
}

export function renderHistoryMarker(
  t: RoundTrip,
  series: AnySeries | null,
  timeToX: TimeToX,
  timeframe: Timeframe,
): ReactNode {
  if (!series) return null;

  // Bucket the fill times to this tab's timeframe so the arrow X-coords
  // resolve to the bar's CENTER, not the exact fraction-of-bar offset where
  // the fill happened. Y remains the exact fill price.
  const entryBarTime = bucketStartSec(t.entryTime * 1000, timeframe);
  const exitBarTime = bucketStartSec(t.exitTime * 1000, timeframe);
  const entryX = timeToX(entryBarTime);
  const exitX = timeToX(exitBarTime);
  const entryY = series.priceToCoordinate(t.entryPrice);
  const exitY = series.priceToCoordinate(t.exitPrice);
  if (entryX == null || exitX == null || entryY == null || exitY == null) {
    return null;
  }

  const isBuy = t.type === "buy";
  const ENTRY_COLOR = "#4fc3f7";
  const exitColor = t.net > 0 ? "#26a69a" : t.net < 0 ? "#ef5350" : "#787b86";
  const pnlText = `${t.net >= 0 ? "+" : ""}${t.net.toFixed(2)}`;
  // Origin-anchored triangle paths — (0,0) is the arrow's tip (the price).
  const arrowUp = "M 0 -7 L -5 3 L 5 3 Z";
  const arrowDown = "M 0 7 L -5 -3 L 5 -3 Z";

  return (
    <g key={`hist-${t.positionId}`} pointerEvents="none">
      {/* Round-trip connector. Dashed + low opacity so it's subordinate. */}
      <line
        x1={entryX}
        y1={entryY}
        x2={exitX}
        y2={exitY}
        stroke={exitColor}
        strokeWidth={1}
        strokeDasharray="3 3"
        opacity={0.55}
      />
      {/* Entry arrow + label */}
      <g transform={`translate(${entryX} ${entryY})`}>
        <path
          d={isBuy ? arrowUp : arrowDown}
          fill={ENTRY_COLOR}
          stroke="#000"
          strokeWidth={0.5}
        />
        <text
          x={9}
          y={0}
          dy="0.32em"
          fill={ENTRY_COLOR}
          fontSize={10}
          fontWeight={600}
          stroke="#000"
          strokeWidth={2.5}
          paintOrder="stroke"
        >
          {`${isBuy ? "BUY" : "SELL"} ${t.volume}  ${pnlText}`}
        </text>
      </g>
      {/* Exit arrow — opposite direction, P&L-colored, no text. */}
      <g transform={`translate(${exitX} ${exitY})`}>
        <path
          d={isBuy ? arrowDown : arrowUp}
          fill={exitColor}
          stroke="#000"
          strokeWidth={0.5}
        />
      </g>
    </g>
  );
}
